<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import OnboardingVisorAPI from 'dashboard/api/onboardingVisor';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { ACCOUNT_ENTRY_PATH } from 'dashboard/constants/globals';
import types from 'dashboard/store/mutation-types';
import { applyColorScheme, isDarkColorScheme } from 'dashboard/helper/themeHelper';
import { STEP_ICONS, progressFor, stepsFor } from './visorSteps';
import { MOTION, SMALL } from './ui';
import StepBoasVindas from './steps/StepBoasVindas.vue';
import StepAparencia from './steps/StepAparencia.vue';
import StepContas from './steps/StepContas.vue';
import StepImoveis from './steps/StepImoveis.vue';
import StepTesteIa from './steps/StepTesteIa.vue';
import StepPainel from './steps/StepPainel.vue';

// Onboarding no padrão Visor (D64): base para toda conta nova e o bloco da
// imobiliária por cima quando a conta tem o Módulo Imobiliária.
const { t } = useI18n();
// Servido pelo Rails (public/); literal em src faria o Vite tentar importar.
const siteupIcon = '/brand-assets/siteup-icon.png';
const route = useRoute();
const router = useRouter();
const store = useStore();

const accountId = computed(() => Number(route.params.accountId));
const state = ref(null);
const properties = ref(null);
const finishing = ref(false);
const current = ref('boas_vindas');

const COMPONENTS = {
  boas_vindas: StepBoasVindas,
  aparencia: StepAparencia,
  contas: StepContas,
  imoveis: StepImoveis,
  teste_ia: StepTesteIa,
  painel: StepPainel,
};

const steps = computed(() =>
  stepsFor({ realEstate: Boolean(state.value?.real_estate) })
);
const index = computed(() => Math.max(0, steps.value.indexOf(current.value)));
const percent = computed(() => progressFor(index.value, steps.value.length));
const done = computed(() => state.value?.progress?.done || []);
const onPanel = computed(() => current.value === 'painel');
const facts = computed(() => state.value?.facts || {});

const loadProperties = async () => {
  try {
    const { data } = await OnboardingVisorAPI.properties();
    properties.value = data;
  } catch {
    properties.value = { total: 0, items: [], error: true };
  }
};

const load = async () => {
  const { data } = await OnboardingVisorAPI.show();
  state.value = data;
  const saved = data.progress?.step;
  if (saved && stepsFor({ realEstate: data.real_estate }).includes(saved)) {
    current.value = saved;
  }
  if (data.real_estate) loadProperties();
};

onMounted(load);

const save = async progress => {
  try {
    const { data } = await OnboardingVisorAPI.save(progress);
    state.value = data;
  } catch {
    useAlert(t('ONBOARDING_VISOR.SAVE_ERROR'));
  }
};

const goTo = step => {
  current.value = step;
  window.scrollTo?.({ top: 0 });
  save({ step });
};

const next = () => {
  const target = steps.value[index.value + 1] || 'painel';
  current.value = target;
  window.scrollTo?.({ top: 0 });
  save({ step: target, done: [...new Set([...done.value, steps.value[index.value]])] });
};

const skipWhatsapp = async () => {
  await save({ whatsapp_skipped: true });
  next();
};

const saveFacts = facts => save({ facts });

const finish = async () => {
  finishing.value = true;
  try {
    await OnboardingVisorAPI.finish();
    store.commit(types.RESET_ONBOARDING, accountId.value);
    router.push(frontendURL(`accounts/${accountId.value}/${ACCOUNT_ENTRY_PATH}`));
  } catch {
    useAlert(t('ONBOARDING_VISOR.SAVE_ERROR'));
    finishing.value = false;
  }
};

const dark = ref(isDarkColorScheme(window.matchMedia?.('(prefers-color-scheme: dark)').matches));
const toggleTheme = () => {
  dark.value = !dark.value;
  applyColorScheme(dark.value ? 'dark' : 'light');
};

// Cada tela recebe só o que declara, para nada vazar como atributo no HTML.
const stepProps = computed(() => {
  const base = { state: state.value };
  if (['contas', 'imoveis', 'teste_ia'].includes(current.value)) {
    base.accountId = accountId.value;
  }
  if (['boas_vindas', 'imoveis', 'painel'].includes(current.value)) {
    base.properties = properties.value;
  }
  if (current.value === 'painel') base.finishing = finishing.value;
  if (current.value === 'aparencia') return {};
  return base;
});

const stepState = (step, i) => {
  if (step === current.value) return 'atual';
  if (done.value.includes(step) || i < index.value) return 'feito';
  return '';
};
</script>

<template>
  <div class="min-h-screen bg-n-background text-n-slate-12 text-[0.9375rem] leading-normal tabular-nums">
    <header class="h-16 flex items-center gap-4 px-7 bg-n-card border-b border-n-weak sticky top-0 z-20 max-md:h-14 max-md:px-4 max-md:gap-2.5">
      <span class="flex items-center gap-2 font-medium text-[1.0625rem] tracking-[-0.02em]">
        <img :src="siteupIcon" alt="" class="size-7" />
        <span>SiteUP <span class="text-n-slate-10">CRM</span></span>
      </span>
      <span class="w-px h-6 bg-n-weak max-md:hidden" />
      <span class="flex items-center gap-2.5 font-medium min-w-0">
        <img v-if="facts.logo_url?.value" :src="facts.logo_url.value" alt="" class="size-[1.875rem] rounded-lg object-contain bg-white" />
        <span class="truncate">
          {{ facts.name?.value }}
          <span v-if="facts.city?.value" class="font-normal max-md:hidden" :class="SMALL">{{ facts.city.value }}</span>
        </span>
      </span>
      <button
        type="button"
        class="ml-auto size-10 rounded-xl border border-n-weak bg-n-card text-n-slate-11 grid place-items-center hover:bg-n-slate-2 flex-none"
        :class="MOTION"
        :aria-label="t('ONBOARDING_VISOR.TOGGLE_THEME')"
        @click="toggleTheme"
      >
        <Icon :icon="dark ? 'i-ph-moon-duotone' : 'i-ph-sun-duotone'" class="size-5" />
      </button>
      <span class="absolute inset-x-0 -bottom-px h-[3px]">
        <i class="block h-[3px] bg-n-blue-9 rounded-r" :class="MOTION" :style="{ width: `${percent}%` }" data-testid="barra-topo" />
      </span>
    </header>

    <div v-if="!state" class="grid place-items-center py-24"><Spinner /></div>

    <div v-else class="md:grid min-h-[calc(100vh-4rem)]" :class="onPanel ? '' : 'md:grid-cols-[16.25rem_1fr]'">
      <aside v-if="!onPanel" class="max-md:hidden px-5 py-8 border-r border-n-weak bg-n-card" data-testid="trilho">
        <p class="px-3 pb-3" :class="SMALL">{{ t('ONBOARDING_VISOR.RAIL_TITLE') }}</p>
        <button
          v-for="(step, i) in steps"
          :key="step"
          type="button"
          class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left"
          :class="[
            MOTION,
            stepState(step, i) === 'atual' ? 'bg-n-blue-3 text-n-slate-12 font-medium' : stepState(step, i) === 'feito' ? 'text-n-slate-11 hover:bg-n-slate-2' : 'text-n-slate-10 cursor-default',
          ]"
          :disabled="!stepState(step, i)"
          :aria-current="step === current ? 'step' : undefined"
          :data-state="stepState(step, i) || 'depois'"
          @click="goTo(step)"
        >
          <span
            class="size-7 rounded-lg grid place-items-center flex-none"
            :class="stepState(step, i) === 'atual' ? 'bg-n-blue-9 text-white' : stepState(step, i) === 'feito' ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-slate-3 text-n-slate-10'"
          >
            <Icon :icon="stepState(step, i) === 'feito' ? 'i-ph-check-circle-duotone' : STEP_ICONS[step]" class="size-4" />
          </span>
          {{ t(`ONBOARDING_VISOR.STEPS.${step.toUpperCase()}`) }}
        </button>
        <div class="mx-3 mt-6 pt-5 border-t border-n-weak">
          <div class="flex justify-between mb-2" :class="SMALL">
            <span>{{ t('ONBOARDING_VISOR.READY') }}</span>
            <b class="text-n-slate-12 font-medium" data-testid="porcentagem">{{ percent }}%</b>
          </div>
          <div class="h-2 rounded-full bg-n-slate-3 overflow-hidden">
            <i class="block h-full rounded-full bg-n-blue-9" :class="MOTION" :style="{ width: `${percent}%` }" />
          </div>
          <p class="mt-2.5" :class="SMALL">{{ t('ONBOARDING_VISOR.READY_HINT') }}</p>
        </div>
      </aside>

      <main class="px-12 pt-10 pb-16 w-full max-md:px-4 max-md:pt-5 max-md:pb-0" :class="onPanel ? 'max-w-[82.5rem] mx-auto' : 'max-w-[73.75rem]'">
        <div v-if="!onPanel" class="md:hidden mb-5">
          <div class="flex justify-between mb-2" :class="SMALL">
            <span>
              {{ t('ONBOARDING_VISOR.STEP_OF', { n: index + 1, total: steps.length }) }}
              <b class="text-n-slate-12 font-medium">{{ t(`ONBOARDING_VISOR.STEPS.${current.toUpperCase()}`) }}</b>
            </span>
            <span>{{ percent }}%</span>
          </div>
          <div class="h-2 rounded-full bg-n-slate-3 overflow-hidden">
            <i class="block h-full rounded-full bg-n-blue-9" :class="MOTION" :style="{ width: `${percent}%` }" />
          </div>
        </div>

        <component
          :is="COMPONENTS[current]"
          :key="current"
          v-bind="stepProps"
          @next="next"
          @save="save"
          @save-facts="saveFacts"
          @skip="skipWhatsapp"
          @go-to="goTo"
          @finish="finish"
          @refresh="load"
        />
      </main>
    </div>
  </div>
</template>
